"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Compass, ArrowRight, House as Home, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const neighborhoods = [{
  name: "Oakhurst",
  tagline: "Historic craftsman & modern rear additions",
  description: "Known for early 20th-century bungalows, wraparound porches, and sympathetic master suite expansions.",
  tags: ["Craftsman Bungalows", "Porch Renovations", "Kitchen Expansions"],
  landmarks: "Near Harmony Park & 4th Ave"
}, {
  name: "Winnona Park",
  tagline: "Classic tudors & finished basements",
  description: "Characterized by brick Tudors and quiet tree-lined avenues requiring preservation-minded architectural care.",
  tags: ["Tudor Restorations", "Basement Finishing", "Historic Millwork"],
  landmarks: "Surrounding Columbia Drive"
}, {
  name: "Great Lakes",
  tagline: "Whole-home remodels & outdoor living",
  description: "Mid-century ranch conversions and two-story second-story additions near local parks and natural greenspaces.",
  tags: ["Second-Story Adds", "Outdoor Living", "Custom Kitchens"],
  landmarks: "North of Decatur Square"
}, {
  name: "Lenox Place",
  tagline: "Cottage additions & custom interiors",
  description: "Period cottages tailored with modern open floor plans, bespoke cabinetry, and primary bathroom suites.",
  tags: ["Bathroom Suites", "Open Concept Living", "Custom Cabinetry"],
  landmarks: "West Decatur corridor"
}, {
  name: "Downtown Decatur",
  tagline: "Historic homes & comprehensive modernizations",
  description: "Urban neighborhood homes within walking distance of the historic square, blending urban convenience and craftsmanship.",
  tags: ["Whole-House Updates", "Architectural Joinery", "Energy Upgrades"],
  landmarks: "Historic Square district"
}];
const extendedCommunities = [{
  name: "Candler Park",
  county: "DeKalb / Fulton"
}, {
  name: "Lake Claire",
  county: "DeKalb"
}, {
  name: "Kirkwood",
  county: "DeKalb"
}, {
  name: "Druid Hills",
  county: "DeKalb"
}, {
  name: "Avondale Estates",
  county: "DeKalb"
}, {
  name: "East Lake",
  county: "DeKalb"
}];
export default function ServiceAreas() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="service-areas" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="bg-accent/40 text-accent-foreground border-border px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
              Service Region
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.05,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-5">
            Serving Decatur & surrounding neighborhoods
          </motion.h2>

          <motion.p initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            From historic craftsman bungalow additions to custom kitchen overhauls and basement suites, our local craftsmen work exclusively across central Decatur and select nearby DeKalb communities.
          </motion.p>
        </div>

        {/* Primary Decatur Neighborhoods Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {neighborhoods.map((item, i) => <motion.div key={item.name} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full bg-card/90 backdrop-blur-md border border-border/80 rounded-xl shadow-xs hover:shadow-md transition-[border-color,box-shadow] hover:border-ring/50 flex flex-col justify-between p-6 sm:p-7">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0 border border-border/60">
                      <MapPin className="size-5" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground bg-muted/60 px-2.5 py-1 rounded border border-border/50">
                      {item.landmarks}
                    </span>
                  </div>

                  <CardHeader className="p-0 mb-3">
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground mb-1">
                      {item.name}
                    </CardTitle>
                    <p className="text-xs font-medium text-accent-foreground tracking-wide">
                      {item.tagline}
                    </p>
                  </CardHeader>

                  <CardContent className="p-0 text-muted-foreground text-sm leading-relaxed mb-6">
                    {item.description}
                  </CardContent>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Home className="size-3.5 text-primary" />
                    <span>Typical Projects</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.tags.map(tag => <span key={tag} className="text-xs bg-secondary/80 text-secondary-foreground px-2.5 py-1 rounded-md border border-border/40 font-medium">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </Card>
            </motion.div>)}

          {/* Quick Consultation Highlight Card in Grid */}
          <motion.div variants={itemVariants} className="h-full" data-index={5}>
            <div className="h-full rounded-xl bg-gradient-to-br from-secondary/80 via-secondary/40 to-background border border-border/80 p-6 sm:p-7 flex flex-col justify-between shadow-xs">
              <div>
                <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4 border border-primary/20">
                  <Compass className="size-5" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground mb-2">
                  Not seeing your street?
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  We frequently accommodate adjacent residential parcels in unincorporated DeKalb and bordering historic enclaves.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-xs font-medium text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>On-site zoning & permitting review</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs font-medium text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Architectural compatibility assessment</span>
                  </li>
                </ul>
              </div>

              <Button asChild variant="outline" className="w-full bg-background hover:bg-secondary text-foreground border-border font-medium shadow-xs">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Inquire About Your Address</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>

        {/* Extended Service Communities Panel */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-card border border-border rounded-xl p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold tracking-wider uppercase text-accent-foreground mb-1 block">
                Extended Metro Service
              </span>
              <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
                Neighboring communities served
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                In addition to the City of Decatur, our dedicated crews handle select full-home, kitchen, and addition projects in neighboring DeKalb and east Atlanta communities.
              </p>
            </div>

            <div className="lg:col-span-7 flex flex-wrap gap-2.5 sm:gap-3 items-center">
              {extendedCommunities.map((community, idx) => <div key={community.name} data-index={idx} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-secondary/70 border border-border text-foreground text-sm font-medium transition-[background-color,border-color] hover:bg-secondary hover:border-ring/40">
                  <MapPin className="size-3.5 text-primary" />
                  <span>{community.name}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    ({community.county})
                  </span>
                </div>)}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border/80 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
              Planning a renovation in these areas? We offer complimentary on-site feasibility consultations.
            </p>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-5 py-2.5 font-medium shrink-0 shadow-xs">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Request Project Estimate</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}